// Shared contract between the VOICER web SPA and the Node proxy.

// ---------------------------------------------------------------------------
// Settings (all live-tunable; persisted by the proxy)
// ---------------------------------------------------------------------------

export type ConversationMode = 'duplex' | 'ptt' | 'text';
export type InstructTemplateId = 'auto' | 'chatml' | 'llama3' | 'gemma' | 'mistral' | 'phi' | 'alpaca';
export type InterleaveMode = 'sentence' | 'full';

/** Sampler / template knobs that vary by loaded GGUF. VAD/TTS live in Settings instead. */
export interface ModelProfileParams {
  instructTemplate: InstructTemplateId;
  interleaveMode: InterleaveMode;
  maxGenTokens: number;
  temperature: number;
  topP: number;
  topK: number;
  minP: number;
  repPen: number;
  /** Extra stop strings merged with the instruct template stops (one per line in the UI). */
  extraStops: string[];
}

export interface ModelProfile extends ModelProfileParams {
  /** Normalized key derived from the KoboldCPP model name. */
  key: string;
  /** Display label (usually the raw model name). */
  label: string;
  updatedAt: number;
}

export const MODEL_PROFILE_KEYS: (keyof ModelProfileParams)[] = [
  'instructTemplate',
  'interleaveMode',
  'maxGenTokens',
  'temperature',
  'topP',
  'topK',
  'minP',
  'repPen',
  'extraStops',
];

export function normalizeModelKey(modelName: string | null | undefined): string {
  return (modelName ?? '')
    .toLowerCase()
    .replace(/^koboldcpp\//, '')
    .replace(/\.(gguf|bin)$/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function profileParamsFromSettings(s: Settings): ModelProfileParams {
  return {
    instructTemplate: s.instructTemplate,
    interleaveMode: s.interleaveMode,
    maxGenTokens: s.maxGenTokens,
    temperature: s.temperature,
    topP: s.topP,
    topK: s.topK,
    minP: s.minP,
    repPen: s.repPen,
    extraStops: [...(s.extraStops ?? [])],
  };
}

/** Backend for a single modality (LLM / STT / TTS). */
export type ModelProviderId = 'kobold' | 'vertex';

export interface Settings {
  koboldUrl: string;
  koboldPassword: string;
  /**
   * Optional separate base URL for Whisper STT (decouples audio from the LLM busy lock).
   * Empty = use koboldUrl.
   */
  sttUrl: string;
  /**
   * Optional separate base URL for TTS.
   * Empty = use koboldUrl.
   */
  ttsUrl: string;
  /**
   * When true, send mid-utterance STT partials during duplex speech for earlier EoT/prepare.
   */
  streamingStt: boolean;
  /** Independent provider per modality (default KoboldCPP). */
  llmProvider: ModelProviderId;
  sttProvider: ModelProviderId;
  ttsProvider: ModelProviderId;
  /** Google Cloud project id (required when any modality is vertex). */
  vertexProjectId: string;
  /** Vertex / regional API location. */
  vertexLocation: string;
  /**
   * Absolute path to a service-account JSON or WIF external_account config on the proxy host.
   * Empty = `GOOGLE_APPLICATION_CREDENTIALS`, then Application Default Credentials (ADC). Never export.
   */
  vertexCredentialsPath: string;
  /**
   * Optional service-account email to impersonate (keyless local auth).
   * Empty = no impersonation. Never export.
   */
  vertexImpersonateServiceAccount: string;
  vertexLlmModel: string;
  vertexSttModel: string;
  vertexTtsModel: string;
  /** Short voice name (e.g. Kore) or full Chirp3-HD id. */
  vertexTtsVoice: string;
  /**
   * Soft cap on Vertex compile prompt size (tokens). Status may still report the model’s
   * full window; interactive turns use min(modelMax, this) so TTFT stays voice-friendly.
   */
  vertexCompileMaxTokens: number;
  mode: ConversationMode;
  ttsEnabled: boolean;
  voice: string;
  ttsLanguage: string;
  /** When true, only speak text inside quotation marks (dialogue); skip narrative. */
  ttsQuotesOnly: boolean;
  instructTemplate: InstructTemplateId;
  interleaveMode: InterleaveMode;
  // Generation
  maxGenTokens: number;
  temperature: number;
  topP: number;
  topK: number;
  minP: number;
  repPen: number;
  /** Extra stop sequences (roleplay models often need User:/*, etc.). */
  extraStops: string[];
  /** When KoboldCPP reports a new model, apply a saved profile for it if one exists. */
  autoApplyModelProfile: boolean;
  // VAD / endpointing
  vadPositiveThreshold: number;
  vadNegativeThreshold: number;
  vadSilenceMs: number;
  vadMinSpeechMs: number;
  vadPreSpeechPadMs: number;
  eotExtendMs: number;
  eotSemanticCheck: boolean;
  /** Silence (after the last speech end) before a high-confidence complete utterance is committed. */
  commitGraceHighMs: number;
  /** Silence (after the last speech end) before a medium-confidence COMPLETE utterance is committed. */
  commitGraceMs: number;
  /** Speech onset within this window after a commit (and before any audio) recalls the turn and merges. */
  continuationRecallMs: number;
  // Barge-in arbiter
  duckLevel: number;
  overlapHardMs: number;
  bargeinMinFrames: number;
  echoCorrelationThreshold: number;
  tierBEnabled: boolean;
  tierBMaxWords: number;
  /** Minimum sustained speech before playback pauses (ms since onset). */
  bargeinPauseAfterMs: number;
  /** Fraction of frames since onset that must be speech-positive to pause. */
  bargeinSpeechRatio: number;
  /** Energy peaks (syllables) required since onset to pause; rejects coughs/claps. */
  bargeinMinSyllables: number;
  /** Segments shorter than this are treated as noise (no STT). */
  bargeinMinSegmentMs: number;
  // TTS pipeline
  prefetchDepth: number;
  firstChunkMinWords: number;
  firstChunkMaxWords: number;
  // Context engine budget (fractions of total budget)
  genReserveTokens: number;
  budgetMemories: number;
  budgetSummary: number;
  budgetRetrieved: number;
  retrievalTopK: number;
  memoryExtraction: boolean;
  backgroundIdleMs: number;
  /**
   * When true, conversations are written to the proxy SQLite file.
   * When false, chat lives in RAM for this process only (gone on restart);
   * settings and model profiles still persist to SQL.
   */
  sqlPersistenceEnabled: boolean;
  // Metrics
  statusPollMs: number;
}

/** Previous interactive default; migrated once to {@link DEFAULT_VERTEX_LLM_MODEL}. */
export const LEGACY_DEFAULT_VERTEX_LLM_MODEL = 'gemini-2.5-flash';
/** Low-latency Vertex chat default (thinking off by default). */
export const DEFAULT_VERTEX_LLM_MODEL = 'gemini-2.5-flash-lite';

/**
 * One-time retarget of the old Flash default to Flash-Lite.
 * Custom model ids (pro, dated previews, etc.) are left unchanged.
 */
export function migrateVertexLlmModel(model: string): string {
  return model.trim() === LEGACY_DEFAULT_VERTEX_LLM_MODEL ? DEFAULT_VERTEX_LLM_MODEL : model;
}

/** Previous voice-oriented reply cap; migrated once to {@link DEFAULT_MAX_GEN_TOKENS}. */
export const LEGACY_DEFAULT_MAX_GEN_TOKENS = 320;
/** Default max reply tokens for case chat / text (was 320 for short voice turns). */
export const DEFAULT_MAX_GEN_TOKENS = 2048;

/** One-time raise of the old 320-token reply cap; other values are left alone. */
export function migrateMaxGenTokens(n: number): number {
  return n === LEGACY_DEFAULT_MAX_GEN_TOKENS ? DEFAULT_MAX_GEN_TOKENS : n;
}

/** Apply one-time settings default migrations; returns the same object when unchanged. */
export function migrateSettingsDefaults(s: Settings): Settings {
  const vertexLlmModel = migrateVertexLlmModel(s.vertexLlmModel);
  const maxGenTokens = migrateMaxGenTokens(s.maxGenTokens);
  if (vertexLlmModel === s.vertexLlmModel && maxGenTokens === s.maxGenTokens) return s;
  return { ...s, vertexLlmModel, maxGenTokens };
}

export const DEFAULT_SETTINGS: Settings = {
  koboldUrl: 'http://localhost:5001',
  koboldPassword: '',
  sttUrl: '',
  ttsUrl: '',
  streamingStt: false,
  llmProvider: 'kobold',
  sttProvider: 'kobold',
  ttsProvider: 'kobold',
  vertexProjectId: '',
  vertexLocation: 'us-central1',
  vertexCredentialsPath: '',
  vertexImpersonateServiceAccount: '',
  vertexLlmModel: DEFAULT_VERTEX_LLM_MODEL,
  vertexSttModel: 'chirp_3',
  vertexTtsModel: 'gemini-2.5-flash-lite-preview-tts',
  vertexTtsVoice: 'Kore',
  vertexCompileMaxTokens: 32_768,
  mode: 'duplex',
  ttsEnabled: true,
  voice: 'kobo',
  ttsLanguage: 'en',
  ttsQuotesOnly: false,
  instructTemplate: 'auto',
  // Sentence interleave: cut at each speakable boundary, run TTS, then continue generation
  // while the client plays — lowest time-to-first-audio. Full = cleanest text, waits for the whole reply.
  interleaveMode: 'sentence',
  maxGenTokens: DEFAULT_MAX_GEN_TOKENS,
  temperature: 0.7,
  topP: 0.92,
  topK: 60,
  minP: 0.05,
  repPen: 1.08,
  extraStops: [],
  autoApplyModelProfile: true,
  vadPositiveThreshold: 0.45,
  vadNegativeThreshold: 0.35,
  vadSilenceMs: 350,
  vadMinSpeechMs: 120,
  vadPreSpeechPadMs: 800,
  eotExtendMs: 900,
  eotSemanticCheck: true,
  commitGraceHighMs: 180,
  commitGraceMs: 350,
  continuationRecallMs: 1500,
  duckLevel: 0.35,
  overlapHardMs: 1500,
  bargeinMinFrames: 3,
  echoCorrelationThreshold: 0.6,
  tierBEnabled: true,
  tierBMaxWords: 8,
  bargeinPauseAfterMs: 300,
  bargeinSpeechRatio: 0.6,
  bargeinMinSyllables: 2,
  bargeinMinSegmentMs: 200,
  prefetchDepth: 3,
  firstChunkMinWords: 5,
  firstChunkMaxWords: 10,
  genReserveTokens: 400,
  budgetMemories: 0.1,
  budgetSummary: 0.15,
  budgetRetrieved: 0.15,
  retrievalTopK: 6,
  memoryExtraction: true,
  backgroundIdleMs: 3000,
  sqlPersistenceEnabled: true,
  statusPollMs: 2000,
};

// ---------------------------------------------------------------------------
// Conversation data
// ---------------------------------------------------------------------------

export type MessageRole = 'user' | 'assistant' | 'aside';

export interface ChatMessage {
  id: string;
  sessionId: string;
  seq: number;
  role: MessageRole;
  content: string;
  ts: number;
  turnId: string;
  /** Previous message on this branch; null = root. */
  parentId?: string | null;
  interrupted?: boolean;
  /** For interrupted assistant messages: the text the user actually heard. */
  heardContent?: string;
  /** For asides: what the assistant was saying at the time. */
  asideContext?: string;
  asideOutcome?: BargeinOutcome;
  tokens?: number;
  excluded?: boolean;
}

export interface PinnedFact {
  id: string;
  sessionId: string;
  text: string;
  position: number;
}

export interface Memory {
  id: string;
  sessionId: string;
  text: string;
  salience: number;
  refCount: number;
  createdAt: number;
  lastUsedAt: number;
  sourceTurnId: string | null;
  pinned: boolean;
  hasEmbedding: boolean;
}

export interface PromptVersion {
  id: string;
  sessionId: string;
  content: string;
  createdAt: number;
  active: boolean;
}

export type CaseStage = 'intake' | 'discovery' | 'trial_prep';

export interface CaseInfo {
  id: string;
  slug: string;
  title: string;
  stage: CaseStage;
  jurisdiction: string;
  synopsis: string;
  partyPlaintiff: string;
  partyDefendant: string;
  matterNumber: string;
  /** Short fact lines for UI / pinned context. */
  keyFacts: string[];
  createdAt: number;
  activeSessionId: string | null;
}

export interface CaseDocumentMeta {
  id: string;
  caseId: string;
  title: string;
  docType: string;
  category: string;
  stageLabel: string;
  sortOrder: number;
  mimeType: string;
  pageCount: number;
  summary: string;
  /** True when a PDF path exists in storage. */
  hasPdf: boolean;
}

export interface SessionInfo {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  messageCount: number;
  /** Short preview of the latest non-aside message. */
  preview: string;
  /** Legal case this conversation belongs to, if any. */
  caseId: string | null;
}

export interface SessionCreateRequest {
  name?: string;
  /** Copy long-term memories from the current session into the new one. */
  preserveMemories?: boolean;
  /** Copy pinned facts / persona. Defaults to true when preserveMemories is true. */
  preservePinned?: boolean;
  /** Copy the active system prompt. Default true. */
  preserveSystemPrompt?: boolean;
  /** Copy the rolling summary (usually off for a fresh chat). */
  preserveSummary?: boolean;
  /** Bind the new session to a legal case (inherits case prompt when set). */
  caseId?: string | null;
}

export interface SessionState {
  session: SessionInfo;
  messages: ChatMessage[];
  /** Tip of the viewed branch (message id). */
  activeLeafId: string | null;
  /** Leaf restored by Forward after Undo; cleared on fork/send/redo. */
  forwardLeafId: string | null;
  pinned: PinnedFact[];
  memories: Memory[];
  summary: string;
  systemPrompt: string;
  promptVersions: PromptVersion[];
  excludedRetrieval: string[];
}

// ---------------------------------------------------------------------------
// Context engine manifest
// ---------------------------------------------------------------------------

export type SegmentKind =
  | 'template'
  | 'system'
  | 'pinned'
  | 'memory'
  | 'summary'
  | 'retrieved'
  | 'doc_chunk'
  | 'turn'
  | 'aside'
  | 'current'
  | 'assistant_prefix';

export interface ContextSegment {
  kind: SegmentKind;
  id: string;
  label: string;
  text: string;
  tokens: number;
  reason: string;
  score?: number;
}

export interface DroppedItem {
  kind: SegmentKind;
  id: string;
  label: string;
  tokens: number;
  reason: string;
}

export interface BudgetShare {
  kind: SegmentKind | 'recent' | 'reserve';
  allocated: number;
  used: number;
}

export interface CompiledContext {
  turnId: string;
  ts: number;
  prompt: string;
  segments: ContextSegment[];
  dropped: DroppedItem[];
  totalTokens: number;
  maxContext: number;
  budget: number;
  shares: BudgetShare[];
  template: Exclude<InstructTemplateId, 'auto'>;
  compileMs: number;
  tokenCountMode: 'kobold' | 'estimate';
}

// ---------------------------------------------------------------------------
// Barge-in arbiter
// ---------------------------------------------------------------------------

export type BargeinOutcome = 'BACKCHANNEL' | 'ANSWER' | 'INTERRUPT' | 'NOISE' | 'ECHO' | 'UNCLEAR';
export type BargeinTier = 'echo' | 'duration' | 'lexical' | 'lexical-interrupt' | 'llm' | 'escalation' | 'playback-ended' | 'recall';

export interface ArbiterEvent {
  id: string;
  sessionId: string;
  turnId: string;
  ts: number;
  transcript: string;
  currentSentence: string;
  tier: BargeinTier;
  outcome: BargeinOutcome;
  onsetToDuckMs: number | null;
  /** Time from onset until playback was paused on sustained speech (null if never paused). */
  onsetToPauseMs: number | null;
  onsetToDecisionMs: number | null;
  onsetToStopMs: number | null;
  sttMs: number | null;
  sttQueueWaitMs: number | null;
  classifyMs: number | null;
  classifyQueueWaitMs: number | null;
  feedback: 'wrong' | null;
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export type TurnMark =
  | 'speechStart'
  | 'speechEnd'
  | 'sttQueued'
  | 'sttSent'
  | 'sttDone'
  | 'eotCheckStart'
  | 'eotCheckDone'
  | 'turnCommitted'
  | 'compileStart'
  | 'compileDone'
  | 'llmQueued'
  | 'llmStart'
  | 'llmFirstToken'
  | 'llmDone'
  | 'ttsFirstQueued'
  | 'ttsFirstSent'
  | 'ttsFirstDone'
  | 'firstAudioOut'
  | 'playbackEnd'
  | 'interrupted';

export interface TurnTimeline {
  turnId: string;
  sessionId: string;
  startedAt: number;
  marks: Partial<Record<TurnMark, number>>;
  promptTokens: number;
  genTokens: number;
  tokensPerSec: number | null;
  llmSegments: number;
  ttsChunks: number;
  ttsMsTotal: number;
  perf?: KoboldPerf | null;
  interrupted: boolean;
  mode: ConversationMode;
  interleave: InterleaveMode;
}

export interface KoboldPerf {
  last_process: number;
  last_eval: number;
  last_token_count: number;
  last_seed: number;
  total_gens: number;
  stop_reason: number;
  queue: number;
  idle: number;
  hordeexitcounter: number;
  uptime: number;
  idletime: number;
  quiet: boolean;
}

export interface StagePercentiles {
  stage: string;
  count: number;
  p50: number;
  p95: number;
  mean: number;
  last: number | null;
}

export interface MetricsSummary {
  turns: number;
  stages: StagePercentiles[];
  arbiter: {
    total: number;
    byOutcome: Record<string, number>;
    byTier: Record<string, number>;
    falseInterrupts: number;
    missedInterrupts: number;
    onsetToDuckP50: number | null;
    onsetToStopP50: number | null;
    classifyP50: number | null;
  };
  scheduler: SchedulerSnapshot;
}

export interface SchedulerSnapshot {
  running: { kind: string; sinceMs: number } | null;
  queued: { kind: string; priority: number; waitedMs: number }[];
  recentQueueWaitMs: Record<string, number>;
}

/** Per-modality provider health (shown in status chrome). */
export interface ProviderModalityStatus {
  provider: ModelProviderId;
  model: string;
  reachable: boolean;
  error: string | null;
}

export interface KoboldStatus {
  reachable: boolean;
  url: string;
  version: string | null;
  model: string | null;
  flags: { llm: boolean; transcribe: boolean; tts: boolean; embeddings: boolean };
  maxContext: number | null;
  perf: KoboldPerf | null;
  template: Exclude<InstructTemplateId, 'auto'> | null;
  voices: string[];
  lastCheckedAt: number;
  error: string | null;
  /** Present when multi-provider settings are active. */
  providers?: {
    llm: ProviderModalityStatus;
    stt: ProviderModalityStatus;
    tts: ProviderModalityStatus;
  };
}

// ---------------------------------------------------------------------------
// WebSocket protocol (request/response + server push)
// ---------------------------------------------------------------------------

export interface RpcRequest<T = unknown> {
  type: string;
  reqId: string;
  data: T;
}

export interface RpcReply<T = unknown> {
  type: 'reply';
  reqId: string;
  ok: boolean;
  data?: T;
  error?: string;
}

/** Metadata for a TTS audio chunk (JSON header of the binary WS frame, or JSON fallback). */
export interface TurnAudioMeta {
  turnId: string;
  index: number;
  text: string;
  ttsMs: number;
  queueWaitMs: number;
  /** Default wav; pcm16 skips browser WAV parse when Kobold returns raw PCM. */
  format?: AudioPayloadFormat;
  sampleRate?: number;
}

/** Client-facing turn.audio payload: binary path sets wavBytes; legacy JSON sets wavBase64. */
export type TurnAudioData = TurnAudioMeta & {
  wavBase64?: string;
  wavBytes?: ArrayBuffer;
};

/** Binary WS frame magic: "VCA1" (Voicer Chunk Audio v1). */
export const AUDIO_FRAME_MAGIC = 'VCA1';

/**
 * Encode turn.audio as: magic(4) + u32be headerLen + JSON meta + raw WAV.
 * Prefer this over JSON+base64 on the hot path.
 */
export function encodeTurnAudioFrame(meta: TurnAudioMeta, wav: Uint8Array): Uint8Array {
  const header = new TextEncoder().encode(JSON.stringify(meta));
  const out = new Uint8Array(8 + header.length + wav.byteLength);
  out[0] = 0x56; // V
  out[1] = 0x43; // C
  out[2] = 0x41; // A
  out[3] = 0x31; // 1
  new DataView(out.buffer, out.byteOffset, out.byteLength).setUint32(4, header.length, false);
  out.set(header, 8);
  out.set(wav, 8 + header.length);
  return out;
}

/** Decode a VCA1 binary frame into a turn.audio data payload, or null if not valid. */
export function decodeTurnAudioFrame(buf: ArrayBuffer): TurnAudioData | null {
  if (buf.byteLength < 8) return null;
  const view = new DataView(buf);
  if (view.getUint8(0) !== 0x56 || view.getUint8(1) !== 0x43 || view.getUint8(2) !== 0x41 || view.getUint8(3) !== 0x31) {
    return null;
  }
  const headerLen = view.getUint32(4, false);
  if (headerLen <= 0 || 8 + headerLen > buf.byteLength) return null;
  let meta: TurnAudioMeta;
  try {
    meta = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 8, headerLen))) as TurnAudioMeta;
  } catch {
    return null;
  }
  if (typeof meta.turnId !== 'string' || typeof meta.index !== 'number') return null;
  return { ...meta, wavBytes: buf.slice(8 + headerLen) };
}

export type ServerEvent =
  | {
      type: 'hello';
      data: {
        session: SessionState;
        settings: Settings;
        status: KoboldStatus;
        sessions: SessionInfo[];
        profiles: ModelProfile[];
        cases: CaseInfo[];
        activeCaseId: string | null;
      };
    }
  | { type: 'status'; data: KoboldStatus }
  | { type: 'settings'; data: Settings }
  | { type: 'profiles'; data: ModelProfile[] }
  | { type: 'session'; data: SessionState }
  | { type: 'sessions'; data: SessionInfo[] }
  | { type: 'cases'; data: { cases: CaseInfo[]; activeCaseId: string | null } }
  | { type: 'message.upsert'; data: ChatMessage }
  | { type: 'message.delete'; data: { id: string } }
  | { type: 'context.compiled'; data: CompiledContext }
  | { type: 'turn.token'; data: { turnId: string; token: string; text: string } }
  | { type: 'turn.sentence'; data: { turnId: string; index: number; text: string } }
  | { type: 'turn.audio'; data: TurnAudioData }
  | { type: 'turn.done'; data: { turnId: string; text: string; interrupted: boolean } }
  | { type: 'turn.error'; data: { turnId: string; error: string } }
  | { type: 'metrics.turn'; data: TurnTimeline }
  | { type: 'metrics.summary'; data: MetricsSummary }
  | { type: 'arbiter.event'; data: ArbiterEvent }
  | { type: 'scheduler'; data: SchedulerSnapshot }
  | { type: 'background'; data: { job: string; state: 'start' | 'done' | 'error'; detail?: string } }
  | { type: 'log'; data: { level: 'info' | 'warn' | 'error'; msg: string; ts: number } };

// RPC payloads ---------------------------------------------------------------

export interface SttRequest {
  lane: 'main' | 'bargein';
  wavBase64: string;
  prompt?: string;
  turnId?: string;
  /** Mid-utterance partial; server may skip heavy side effects. */
  partial?: boolean;
}
export interface SttResult {
  text: string;
  sttMs: number;
  queueWaitMs: number;
  /** Echo of partial flag for client merge logic. */
  partial?: boolean;
}

export interface TurnStartRequest {
  turnId: string;
  text: string;
  marks: Partial<Record<TurnMark, number>>;
  /** Text-only turns skip TTS regardless of settings when false. */
  wantAudio: boolean;
  /** When true, do not insert a new user message (used for regenerate). */
  reuseUserText?: boolean;
  /** Existing user message to reuse when regenerating (assistant becomes its child). */
  reuseUserMessageId?: string;
  /** Update this existing user message with `text` instead of inserting (continuation recall). */
  replaceUserMessageId?: string;
}

export interface TurnInterruptRequest {
  turnId: string;
  heardText: string;
  reason: BargeinTier;
}

export interface BargeinClassifyRequest {
  turnId: string;
  transcript: string;
  currentSentence: string;
}
export interface BargeinClassifyResult {
  outcome: BargeinOutcome;
  classifyMs: number;
  queueWaitMs: number;
  raw: string;
}

export interface EotCheckRequest {
  text: string;
}
export type EotConfidence = 'high' | 'medium' | 'low';

export interface EotCheckResult {
  complete: boolean;
  tier: 'lexical' | 'llm';
  /** high = terminal punct / known complete reply; medium = other COMPLETE; low = INCOMPLETE. */
  confidence: EotConfidence;
  ms: number;
}

export interface TurnPrepareRequest {
  text: string;
}

export interface AsideRequest {
  turnId: string;
  text: string;
  outcome: BargeinOutcome;
  currentSentence: string;
}

export interface AudioMarkRequest {
  turnId: string;
  index: number;
  mark: 'started' | 'ended';
  ts: number;
}

export interface ContextDryRunRequest {
  text: string;
}

export interface ExportBundle {
  version: 1 | 2;
  exportedAt: number;
  session: SessionState;
  arbiterEvents: ArbiterEvent[];
  turns: TurnTimeline[];
  /** v2: full live settings (including koboldUrl / password). */
  settings?: Settings;
  /** v2: saved per-model profiles. */
  profiles?: ModelProfile[];
}

export const STAGE_DEFS: { key: string; label: string; from: TurnMark; to: TurnMark }[] = [
  { key: 'stt', label: 'STT (sent to done)', from: 'sttSent', to: 'sttDone' },
  { key: 'sttQueue', label: 'STT queue wait', from: 'sttQueued', to: 'sttSent' },
  { key: 'endpoint', label: 'Speech end to commit', from: 'speechEnd', to: 'turnCommitted' },
  { key: 'compile', label: 'Context compile', from: 'compileStart', to: 'compileDone' },
  { key: 'llmQueue', label: 'LLM queue wait', from: 'llmQueued', to: 'llmStart' },
  { key: 'ttft', label: 'LLM time to first token', from: 'llmStart', to: 'llmFirstToken' },
  { key: 'llm', label: 'LLM total', from: 'llmStart', to: 'llmDone' },
  { key: 'ttsFirst', label: 'First TTS chunk', from: 'ttsFirstSent', to: 'ttsFirstDone' },
  { key: 'ttfa', label: 'Time to first audio (speech end)', from: 'speechEnd', to: 'firstAudioOut' },
  { key: 'e2e', label: 'End to end (commit to first audio)', from: 'turnCommitted', to: 'firstAudioOut' },
  { key: 'playback', label: 'Playback duration', from: 'firstAudioOut', to: 'playbackEnd' },
];

export function newId(prefix = ''): string {
  const rnd = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  return prefix ? `${prefix}_${rnd}` : rnd;
}

export {
  applySettingsPatch,
  clampBudgetShares,
  validateProfileParams,
  validateSettingsPatch,
} from './validate.ts';

/** Binary WS frame magic: "VST1" (Voicer STT audio v1) — Int16 LE PCM @ sampleRate. */
export const STT_FRAME_MAGIC = 'VST1';

export interface SttAudioMeta {
  lane: 'main' | 'bargein';
  sampleRate: number;
  prompt?: string;
  turnId?: string;
  /** Incremental / partial STT (mid-utterance). */
  partial?: boolean;
  reqId: string;
}

/**
 * Encode STT audio as: magic(4) + u32be headerLen + JSON meta + Int16 LE PCM.
 */
export function encodeSttAudioFrame(meta: SttAudioMeta, pcm: Int16Array): Uint8Array {
  const header = new TextEncoder().encode(JSON.stringify(meta));
  const pcmBytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
  const out = new Uint8Array(8 + header.length + pcmBytes.byteLength);
  out[0] = 0x56; // V
  out[1] = 0x53; // S
  out[2] = 0x54; // T
  out[3] = 0x31; // 1
  new DataView(out.buffer, out.byteOffset, out.byteLength).setUint32(4, header.length, false);
  out.set(header, 8);
  out.set(pcmBytes, 8 + header.length);
  return out;
}

/** Decode a VST1 binary frame, or null if not valid. */
export function decodeSttAudioFrame(buf: ArrayBuffer): { meta: SttAudioMeta; pcm: Int16Array } | null {
  if (buf.byteLength < 8) return null;
  const view = new DataView(buf);
  if (view.getUint8(0) !== 0x56 || view.getUint8(1) !== 0x53 || view.getUint8(2) !== 0x54 || view.getUint8(3) !== 0x31) {
    return null;
  }
  const headerLen = view.getUint32(4, false);
  if (headerLen <= 0 || 8 + headerLen > buf.byteLength) return null;
  let meta: SttAudioMeta;
  try {
    meta = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 8, headerLen))) as SttAudioMeta;
  } catch {
    return null;
  }
  if (meta.lane !== 'main' && meta.lane !== 'bargein') return null;
  if (typeof meta.reqId !== 'string') return null;
  const pcmBytes = new Uint8Array(buf, 8 + headerLen);
  if (pcmBytes.byteLength % 2 !== 0) return null;
  // Copy into a fresh buffer so Int16Array offset is always aligned.
  const aligned = new ArrayBuffer(pcmBytes.byteLength);
  new Uint8Array(aligned).set(pcmBytes);
  const pcm = new Int16Array(aligned);
  return { meta, pcm };
}

/** Convert Float32 [-1,1] mono samples to Int16 LE PCM. */
export function floatToInt16Pcm(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    out[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
  }
  return out;
}

/** Convert Int16 PCM back to Float32 for WAV encoding / Whisper clients that want float. */
export function int16PcmToFloat(pcm: Int16Array): Float32Array {
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / (pcm[i] < 0 ? 0x8000 : 0x7fff);
  return out;
}

/** Build a mono WAV (base64) from Int16 PCM — used by the proxy when decoding VST1 frames. */
export function encodeWavBase64FromInt16(pcm: Int16Array, sampleRate: number): string {
  const dataSize = pcm.byteLength;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  new Uint8Array(buffer, 44).set(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength));
  const bytes = new Uint8Array(buffer);
  // Prefer Node Buffer when present; otherwise browser btoa.
  const Buf = (globalThis as unknown as { Buffer?: { from(b: Uint8Array): { toString(enc: string): string } } }).Buffer;
  if (Buf) return Buf.from(bytes).toString('base64');
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** TurnAudioMeta may carry raw PCM16 for lower decode latency. */
export type AudioPayloadFormat = 'wav' | 'pcm16';

export {
  activePath,
  canUndoTurn,
  deepestDescendant,
  leafAfterStepBack,
  siblingIndex,
  siblings,
  spineToRoot,
  turnSteps,
  type TurnStep,
} from './messageTree.ts';

export {
  composeSystemPrompt,
  extractSillyTavernJsonFromPng,
  mapCardToImport,
  normalizeSillyTavernCard,
  parseSillyTavernCard,
  parseSillyTavernCardJson,
  type NormalizedSillyTavernCard,
  type SillyTavernBookEntry,
  type SillyTavernCardImport,
  type SillyTavernImportMode,
} from './sillytavern/index.ts';
